
import React, { useState, useEffect } from 'react';
import { useLanguage, type Language } from '@/contexts/LanguageContext';
import { useTranslatedContent } from '@/hooks/useTranslatedContent';

/**
 * Hook for translating entire page content
 * Uses static translations defined in the application
 * @param pagePath The path of the current page
 */
export function usePageTranslation(
  pagePath: string
): { isLoading: boolean } {
  const [isLoading, setIsLoading] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // When the language changes, we just need to trigger a brief loading state
    // to allow components to show loading indicators if needed
    setIsLoading(true);
    
    // Simulate a brief loading period for UX consistency
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [language, pagePath]);

  return { isLoading };
}

// We export page paths to allow consistent usage across the app
export const PAGE_PATHS = {
  HOME: '/',
  GALLERY: '/galerie',
  EQUIPMENT: '/equipements',
  BOOKING: '/reserver',
  CONTACT: '/contact',
  SURROUNDINGS: '/environs',
  ACCESS: '/acces',
  LEGAL: '/mentions-legales',
};

/**
 * Component that wraps text content to provide translations
 * @deprecated Use the standalone TranslatedText component from '@/components/TranslatedText' instead
 */
export function TranslatedText({ children, skipTranslation = false }: { 
  children: string;
  skipTranslation?: boolean;
}) {
  const { translatedContent } = useTranslatedContent(children, { skipTranslation });
  return <>{translatedContent}</>;
}
